import { useState } from 'react';

import { CancelToken } from 'axios';
import { debounce, isFunction, isEmpty } from 'lodash';
import { useQuery } from 'react-query';

import { debounceInterval as debounceIntervalValue } from 'constants/debounceInterval';
import { formatOrder } from 'selectors/paginatedRestApi';

import addDevelopmentData from 'utils/addDevelopmentData';

/**
 * Use fetch hook
 * provide state management for sorting/ordering
 * provide state of loading
 *
 * @param  {function} apiFuncName - api function name
 * @param  {function} apiFunc - api function to call
 * @param  {Object} filters - formatted filter that is ready to be plugged into any query directly
 * @param  {string} searchTerm - value that to be added to search query param
 * @param  {string} searchField - field name that to be added to search query param
 * @param  {string} defaultOrder - asc/desc
 * @param  {string} defaultOrderBy - ordering field name
 * @param  {Object} apiFuncParams - params for the api function
 * @param  {number} debounceInterval - search term debounce interval
 * @param  {Object} options - query options (onSuccess, onError, queryKey, select, ...)
 */
const useFetch = ({
  apiFuncName,
  apiFunc,
  filters = {},
  searchTerm = '',
  searchField = 'search',
  defaultOrder = '',
  defaultOrderBy = '',
  apiFuncParams = {},
  debounceInterval = debounceIntervalValue,
  options = {},
  developmentAdjustFunc,
} = {}) => {
  const [order, setOrder] = useState(defaultOrder);
  const [orderBy, setOrderBy] = useState(defaultOrderBy);
  const [searchString, setSearchString] = useState('');

  const queryKeyAdditionalParams = [
    filters,
    searchString,
    order,
    orderBy,
    apiFuncParams,
  ].filter(item => !isEmpty(item));

  let queryKey = apiFuncName;

  if (!isEmpty(queryKeyAdditionalParams)) {
    queryKey = [apiFuncName, queryKeyAdditionalParams];
  }

  const queryFn = () => {
    const source = CancelToken.source();

    const search = searchString || searchTerm;
    const params = {
      ...filters,
    };

    if (search) {
      params[searchField] = search;
    }

    if (orderBy) {
      params.ordering = formatOrder({ order, orderBy });
    }

    const promise = apiFunc({
      ...apiFuncParams,
      params,
      cancelToken: source.token,
    });
    promise.cancel = () => {
      source.cancel('Query was cancelled by React Query');
    };
    return promise;
  };

  const {
    data,
    error,
    isSuccess,
    isLoading,
    isError,
    isFetching,
    isStale,
    refetch,
  } = useQuery({
    queryKey,
    queryFn,
    ...options,
  });

  const debouncedSetSearchString = debounce(setSearchString, debounceInterval);

  const hasMockData = isFunction(developmentAdjustFunc);

  return {
    data: hasMockData
      ? addDevelopmentData({ rawData: data, developmentAdjustFunc })
      : data,
    error,
    isError,
    isFetching,
    isLoading,
    isSuccess,
    isStale,
    order,
    orderBy,
    refetch,
    searchString,
    setOrder,
    setOrderBy,
    setSearchString: debouncedSetSearchString,
    queryKey,
  };
};

export default useFetch;
